<template>
  <ProjectSection
    id="experience"
    title="Experience"
    tagline="Where I've studied, worked, taught and led."
    chip="Open to entry-level software roles"
    accent="#e4e4e7"
  >
    <template #emblem>
      <div class="emblem-rings">
        <span v-for="n in 4" :key="n" class="er" :style="{ '--n': n }"></span>
        <span class="er-core">B</span>
      </div>
    </template>

    <!-- Education -->
    <article v-reveal class="card span-12 edu-card">
      <div class="edu-main">
        <div class="edu-badge"><font-awesome-icon :icon="['fas', 'building-columns']" /></div>
        <div>
          <h3>University of Maryland, College Park</h3>
          <p>B.S. Computer Science · Expected May 2027</p>
        </div>
      </div>
      <div class="edu-stats">
        <div><strong>3.5</strong><small>GPA</small></div>
        <div><strong>Dean's List</strong><small>Spring 2025</small></div>
      </div>
      <div class="edu-courses">
        <span v-for="c in courses" :key="c.code" class="course">
          <span class="mono">{{ c.code }}</span>{{ c.name }}
        </span>
      </div>
    </article>

    <!-- Roles -->
    <article
      v-for="(job, i) in jobs"
      :key="job.org"
      v-reveal="(i % 2) * 80"
      class="card span-6 job-card"
    >
      <div class="job-top">
        <span class="job-icon" :style="{ '--c': job.color }"><font-awesome-icon :icon="['fas', job.icon]" /></span>
        <span class="mono job-dates">{{ job.dates }}</span>
      </div>
      <h3>{{ job.org }}</h3>
      <div class="job-role">{{ job.role }}</div>
      <ul>
        <li v-for="b in job.points" :key="b">{{ b }}</li>
      </ul>
    </article>

    <!-- Toolbox -->
    <article v-reveal class="card span-12 tool-card">
      <h3>Toolbox</h3>
      <p>The languages, frameworks and tools I use every week.</p>
      <div class="tools">
        <div v-for="t in tools" :key="t.name" class="tool" :title="t.name">
          <span class="tool-tile">
            <font-awesome-icon v-if="t.icon" :icon="t.icon" />
            <span v-else class="tool-text">{{ t.text }}</span>
          </span>
          <span class="tool-name">{{ t.name }}</span>
        </div>
      </div>
    </article>
  </ProjectSection>
</template>

<script>
import ProjectSection from '../ProjectSection.vue';

export default {
  name: 'ExperienceSection',
  components: { ProjectSection },
  data() {
    return {
      courses: [
        { code: 'CMSC421', name: 'Artificial Intelligence' },
        { code: 'CMSC424', name: 'Database Design' },
        { code: 'CMSC351', name: 'Algorithms' },
        { code: 'STAT400', name: 'Probability & Statistics' },
      ],
      jobs: [
        {
          org: 'Peachtree Group with TradeChoice',
          role: 'Technical Intern',
          dates: 'Jul – Sep 2024',
          icon: 'chart-line',
          color: '#34d399',
          points: [
            'Built Python and Pandas automation that loads borrower financial data into the asset-management model senior lenders use.',
            'Designed UI and UX improvements for the asset-management site based on what analysts asked for.',
            'Added input checks before DealCloud CRM uploads to cut rework.',
          ],
        },
        {
          org: 'National Institute of Standards and Technology',
          role: 'Technical Intern',
          dates: 'Jun – Sep 2024',
          icon: 'flask',
          color: '#3b82f6',
          points: [
            'Tagged and cataloged research media in a 400,000+ item collection for the Disaster and Failure Studies Program.',
            'Kept research records and databases organized with the team and management.',
          ],
        },
        {
          org: 'Panda Programmer',
          role: 'Assistant Manager & Lead Teacher',
          dates: 'Oct 2021 – Mar 2026',
          icon: 'chalkboard-user',
          color: '#f59e0b',
          points: [
            'Built Electron, Google API and Playwright tools that saved staff ~10 hours of manual work a week.',
            'Coordinated equipment and support across 26+ schools and 20+ teachers.',
            'Trained teachers and taught Scratch, Python, JavaScript and Java to a school of 300+ students.',
          ],
        },
        {
          org: 'Montgomery Sports Association',
          role: 'Coding & Robotics Instructor',
          dates: 'Jan 2025 – Present',
          icon: 'robot',
          color: '#8b5cf6',
          points: [
            'Lead after-school Scratch coding and robotics classes for elementary and middle schoolers.',
            'Adapt each lesson to the student, from first-time coders to kids ready for harder projects.',
          ],
        },
      ],
      tools: [
        { name: 'Python', icon: ['fab', 'python'] },
        { name: 'C++', text: 'C++' },
        { name: 'CUDA', text: 'CU' },
        { name: 'Swift', icon: ['fab', 'swift'] },
        { name: 'JavaScript', icon: ['fab', 'js'] },
        { name: 'Java', icon: ['fab', 'java'] },
        { name: 'SQL', icon: ['fas', 'database'] },
        { name: 'Dart / Flutter', text: 'Dt' },
        { name: 'Node.js', icon: ['fab', 'node-js'] },
        { name: 'Vue.js', icon: ['fab', 'vuejs'] },
        { name: 'SwiftUI', icon: ['fab', 'apple'] },
        { name: 'Pandas', text: 'pd' },
        { name: 'Git', icon: ['fab', 'git-alt'] },
        { name: 'Figma', icon: ['fab', 'figma'] },
        { name: 'LLM APIs', icon: ['fas', 'wand-magic-sparkles'] },
        { name: 'Tailscale', icon: ['fas', 'network-wired'] },
      ],
    };
  },
};
</script>

<style scoped>
/* Emblem */
.emblem-rings {
  position: relative;
  display: grid;
  place-items: center;
  width: 150px;
  height: 150px;
}

.er {
  position: absolute;
  inset: calc((var(--n) - 1) * 16px);
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, calc(0.08 + var(--n) * 0.06));
  animation: breathe 4s ease-in-out infinite;
  animation-delay: calc(var(--n) * -0.6s);
}

@keyframes breathe {
  50% { transform: scale(1.06); opacity: 0.5; }
}

.er-core {
  display: grid;
  place-items: center;
  width: 58px;
  height: 58px;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 28%, #fff, #d4d4d8 60%, #8b8b93);
  color: #141417;
  font-size: 32px;
  font-weight: 700;
  box-shadow: 0 0 40px rgba(255, 255, 255, 0.2);
}

/* Education */
.edu-card {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 24px;
  min-height: 0;
}

.edu-main { display: flex; align-items: center; gap: 18px; }

.edu-badge {
  display: grid;
  place-items: center;
  flex: none;
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: linear-gradient(160deg, #ef4444, #991b1b);
  box-shadow: inset 0 2px 1px rgba(255, 255, 255, 0.3);
  font-size: 24px;
}

.edu-main h3 { margin-bottom: 2px; }

.edu-stats { display: flex; gap: 36px; text-align: right; }
.edu-stats strong { display: block; font-size: 24px; font-weight: 700; }
.edu-stats small { color: var(--text-dim); font-size: 13px; }

.edu-courses {
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.course {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px 8px 8px;
  border-radius: 12px;
  background: #121215;
  border: 1px solid var(--line);
  font-size: 14px;
  color: #d4d4d8;
}

.course .mono {
  padding: 3px 8px;
  border-radius: 7px;
  background: rgba(255, 255, 255, 0.06);
  color: var(--text-dim);
  font-size: 12px;
}

/* Jobs */
.job-card { min-height: 0; }

.job-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 22px;
}

.job-icon {
  display: grid;
  place-items: center;
  width: 46px;
  height: 46px;
  border-radius: 14px;
  background: color-mix(in srgb, var(--c) 16%, transparent);
  border: 1px solid color-mix(in srgb, var(--c) 35%, transparent);
  color: var(--c);
  font-size: 19px;
}

.job-dates { color: var(--text-faint); font-size: 12px; }
.job-role { color: var(--text-dim); font-size: 14px; margin-bottom: 18px; }

.job-card ul {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.job-card li {
  position: relative;
  padding-left: 18px;
  font-size: 14px;
  line-height: 1.55;
  color: #b4b4bc;
}

.job-card li::before {
  content: '';
  position: absolute;
  left: 2px;
  top: 9px;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--text-faint);
}

/* Toolbox */
.tool-card { min-height: 0; }

.tools {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 18px 12px;
  margin-top: 28px;
}

.tool {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.tool-tile {
  display: grid;
  place-items: center;
  width: 64px;
  height: 64px;
  border-radius: 18px;
  background: linear-gradient(160deg, #2c2c31, #1a1a1d);
  border: 1px solid var(--line-strong);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 8px 20px rgba(0, 0, 0, 0.35);
  color: #e4e4e7;
  font-size: 26px;
  transition: transform 0.3s var(--ease), border-color 0.3s;
}

.tool:hover .tool-tile {
  transform: translateY(-4px);
  border-color: rgba(255, 255, 255, 0.25);
}

.tool-text { font-size: 18px; font-weight: 700; letter-spacing: -0.02em; }
.tool-name { font-size: 12px; color: var(--text-dim); text-align: center; }

@media (max-width: 900px) {
  .edu-card { grid-template-columns: 1fr; }
  .edu-stats { text-align: left; }
  .tools { grid-template-columns: repeat(4, 1fr); }
}
</style>
